import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { API_BASE_URL } from '../../services/apiClient';

const REASON_MIN_LENGTH = 5;
const REASON_MAX_LENGTH = 300;

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

// Admin → Usuarios Registrados → Cancelar. The backend cancels in Stripe
// first (no refund) and only then updates the user — see
// backend/services/admin_cancellation.py.
const AdminCancelUserDialog = ({ user, open, onOpenChange, headers, onDone }) => {
  const [mode, setMode] = useState('immediate');
  const [reason, setReason] = useState('');
  const [blockAccount, setBlockAccount] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode('immediate');
      setReason('');
      setBlockAccount(false);
      setConfirmEmail('');
    }
  }, [open]);

  if (!user) return null;

  const hasPlan = Boolean(user.plan_tier) && user.plan_status !== 'canceled';
  const paymentPending = ['past_due', 'unpaid'].includes(user.plan_status);
  const periodEnd = formatDay(user.current_period_end);
  const emailMatches = confirmEmail.trim().toLowerCase() === (user.email || '').toLowerCase();
  const canSubmit =
    !submitting &&
    emailMatches &&
    reason.trim().length >= REASON_MIN_LENGTH &&
    (hasPlan || blockAccount);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/admin/users/${user.id}/cancel`,
        { mode, reason: reason.trim(), block_account: blockAccount, confirm_email: confirmEmail.trim() },
        { headers }
      );
      toast.success(res.data.message);
      onDone(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cancelar al usuario');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" /> Cancelar usuario
          </DialogTitle>
          <DialogDescription>
            {user.name || 'Sin nombre'} · {user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          <div className="rounded-lg border border-border p-3 space-y-1">
            {hasPlan ? (
              <>
                <p>
                  Plan <strong>{user.plan_tier}</strong> · {user.has_stripe ? 'cobrado en Stripe' : 'otorgado por admin'} · estado{' '}
                  <strong>{user.plan_status}</strong>
                </p>
                {periodEnd && <p className="text-muted-foreground">Pagado hasta el {periodEnd}</p>}
                {user.cancel_at_period_end && (
                  <p className="text-amber-500">Ya está programada para cancelarse al final del periodo.</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">No tiene una suscripción activa: solo se puede bloquear la cuenta.</p>
            )}
          </div>

          {hasPlan && (
            <div>
              <Label className="mb-2 block">¿Cuándo termina el acceso?</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="space-y-1">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="immediate" id="admin-cancel-immediate" />
                  <Label htmlFor="admin-cancel-immediate" className="font-normal cursor-pointer">
                    Cancelar ahora (pierde el acceso de inmediato)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="period_end" id="admin-cancel-period-end" disabled={paymentPending} />
                  <Label
                    htmlFor="admin-cancel-period-end"
                    className={`font-normal ${paymentPending ? 'text-muted-foreground' : 'cursor-pointer'}`}
                  >
                    Al final del periodo{periodEnd ? ` (${periodEnd})` : ''}
                  </Label>
                </div>
              </RadioGroup>
              {paymentPending && (
                <p className="text-xs text-muted-foreground mt-1">
                  Tiene un pago pendiente: solo se puede cancelar de inmediato para que Stripe deje de reintentar el cobro.
                </p>
              )}
              {user.has_stripe && (
                <p className="text-xs text-muted-foreground mt-2">
                  No se realiza reembolso. Si corresponde, hazlo manualmente en el dashboard de Stripe.
                </p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="admin-cancel-reason" className="mb-1 block">Motivo (obligatorio)</Label>
            <Textarea
              id="admin-cancel-reason"
              value={reason}
              maxLength={REASON_MAX_LENGTH}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: el cliente pidió la baja por soporte"
              rows={2}
            />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox id="admin-cancel-block" checked={blockAccount} onCheckedChange={(v) => setBlockAccount(v === true)} />
            <Label htmlFor="admin-cancel-block" className="font-normal cursor-pointer leading-snug">
              Bloquear también la cuenta (cierra su sesión, no puede volver a entrar y se detiene la sincronización)
            </Label>
          </div>

          <div>
            <Label htmlFor="admin-cancel-confirm" className="mb-1 block">
              Escribe <strong>{user.email}</strong> para confirmar
            </Label>
            <Input
              id="admin-cancel-confirm"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Volver
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="admin-cancel-confirm"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {hasPlan ? 'Cancelar suscripción' : 'Bloquear cuenta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCancelUserDialog;
